import { memo, useState } from 'react';

import { LoaderTraceLogo } from '@stratumn/atomic';

import filesize from 'filesize';
import { Scroll } from 'lucide-react';

import { cn } from '@/shadcn/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/shadcn/tooltip';

import { Ellipsis } from '@/components/ui/ellipsis/ellipsis';
import { downloadFile } from '@/utils/downloadFile';

type Props = {
  data: {
    name: string;
    size: number;
  };
  readonly?: boolean;
};

export const DownloadFile = memo(({ data, readonly = false }: Props) => {
  const [loading, setLoading] = useState(false);

  if (loading) return <LoaderTraceLogo />;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            aria-label="Click to download documentation"
            className={cn(
              'flex items-center gap-2 overflow-hidden',
              !readonly && 'cursor-pointer'
            )}
            data-is-downloadable={!readonly}
            onClick={
              readonly
                ? undefined
                : () => downloadFile(data, setLoading, loading)
            }
          >
            <Scroll className="block w-auto shrink-0" size={16} />
            <div
              className="ml-1 flex flex-col items-start overflow-hidden text-xs tracking-wide"
              data-cy="download-documentation"
            >
              <div className="max-w-full capitalize">
                <Ellipsis>{data.name}</Ellipsis>
              </div>

              <div className="max-w-full text-[10px] text-muted-foreground uppercase">
                {filesize(data.size)}
              </div>
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent>Click to download documentation</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});

export default DownloadFile;
